import {Analytics} from "@plaudit/analytics";

declare global {
	interface Window {
		aoPostSubmitFormCallback: any;
		ActOnForm_TrackingNames: any;
	}
}

/**
 * This class searches the document for all Act-On forms and reports the form's submission to analytics (e.g. Google Analytics).
 * This code is used both with embedded forms on seeclearfield.com and also landing pages hosted by Act-On.
 * This is done by modifying window.aoPostSubmitFormCallback which is an array of functions. The key to the array
 * is the ID Act-On uses for each form.
 * We monitor the dom for any forms with the "ao-form" style class and do our setup work when one is found.
 * Since Act-On does not provide us the human readable name of the form we have the option of adding it to the page
 * using an array on `window` which is called ActOnForm_TrackingNames. The key is the same ID. If this is not set, we use the page title.
 * How to store a form's tracking id:
 * <script>
 * 		window.ActOnForm_TrackingNames = window.ActOnForm_TrackingNames || {};
 * 		window.ActOnForm_TrackingNames["FORM ID HERE"] = "Name to track form as";
 * </script>
 */
export default class ActOnForm {
	private static mutationObserver: MutationObserver;
	static forms: HTMLCollectionOf<Element>;

	private static readonly actOnStyleClass = "ao-form";
	private static readonly actOnStyleClassSetup = "actOnForm--isSetup";

	public static init() {
		if (ActOnForm.mutationObserver === undefined) {
			ActOnForm.mutationObserver = new MutationObserver(this.onMutation);
			ActOnForm.mutationObserver.observe(window.document.documentElement, {
				childList: true,
				subtree: true,
				characterData: false,
				attributes: false
			});
			ActOnForm.onMutation();
		}
	}
	private static getActOnForms() {
		if (ActOnForm.forms === undefined) {
			ActOnForm.forms = window.document.documentElement.getElementsByClassName(ActOnForm.actOnStyleClass);
		}
		return ActOnForm.forms;
	}
	private static readonly onMutation = () => {
		const forms = ActOnForm.getActOnForms();
		for (let i=0; i < forms.length; i++) {
			const formElement = forms[i];
			if (!formElement.classList.contains(ActOnForm.actOnStyleClassSetup) && formElement instanceof HTMLFormElement) {
				new ActOnForm(formElement);
				formElement.classList.add(ActOnForm.actOnStyleClassSetup);
			}
		}
	}


	// ====================================================================================================


	private readonly id: string;
	private readonly originalOnSubmit: () => {} = null;

	constructor(formElement: HTMLFormElement) {
		// Get the id that Act-On uses for this form
		// This code is a copy from Act-On's form_embed.js
		var formId = formElement.id.replace("ao-form-", "");
		formId = ( formId.indexOf(':') > -1 ) ? formId.substr( 0, formId.indexOf(':') ) : formId;
		this.id = formId;

		//
		const submitUrlField = formElement.querySelector("[name=submitUrl]");
		if (submitUrlField !== null && submitUrlField instanceof HTMLInputElement) {
			submitUrlField.value = document.location.href;
		}

		// aoPostSubmitFormCallback is an object that Act-On uses.
		// Store the current function; and call it after our onSubmit if not undefined
		if (window.aoPostSubmitFormCallback == undefined) {
			window.aoPostSubmitFormCallback = {};
		}
		if (window.aoPostSubmitFormCallback[this.id] !== undefined) {
			this.originalOnSubmit = window.aoPostSubmitFormCallback[this.id];
		}
		window.aoPostSubmitFormCallback[this.id] = this.onSubmit;
	}

	private readonly onSubmit = () => {
		const analytics = Analytics.getInstance();
		analytics.trackEvent(
			"Act-On", // category
			"Form Submit", // action
			this.getTrackingName() // label
		);

		if (this.originalOnSubmit !== null) {
			this.originalOnSubmit();
		}
	}

	private getTrackingName(): string {
		// Act-On does not have a way for us to get the name of the form so:
		// 1) We will store the value we want to use
		// 2) If one is not stored on the page, we fallback to using the page's title. We remove anything from "|" or after.
		let trackingName: string;
		
		if (window.ActOnForm_TrackingNames !== undefined && window.ActOnForm_TrackingNames[this.id] !== undefined) {
			trackingName = window.ActOnForm_TrackingNames[this.id];
		}
		
		if (trackingName === undefined || trackingName === null || trackingName.length === 0) {
			const indexOfPipe = window.document.title.lastIndexOf("|");
			if (indexOfPipe > 1) {
				trackingName = window.document.title.substring(0, indexOfPipe).trim();
			} else {
				trackingName = window.document.title;
			}
		}

		return trackingName;
	}
}

